/**
 * Neutral RGB ↔ hex colour codec (one truth).
 *
 * A pure, dependency-free leaf shared by the two render backends that each used
 * to carry their own identical copy: the PDF plot path (`print/pdf/*`) and the
 * WebGL scene path (`scene3d/hatch/*`). The two backends keep their OWN colour
 * *models* (the PDF plot tweaks white→black for paper; the scene resolvers stay
 * faithful) — only this generic `#rrggbb` ⇄ `{r,g,b}` (0..1) codec + the
 * `RgbColor` shape are shared, so neither subsystem depends on the other.
 */

/** RGB triple, each component in 0..1. */
export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

/**
 * Parse a `#rrggbb` (optional leading `#`) hex string into an {@link RgbColor}
 * with components in 0..1. Returns black (`{0,0,0}`) for a malformed / non-6-hex
 * input — both prior copies used this same fallback (`PLOT_BLACK` on the PDF
 * side is `{r:0,g:0,b:0}`).
 */
export function hexToRgb(hex: string): RgbColor {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1]!, 16);
  return {
    r: ((n >> 16) & 0xff) / 255,
    g: ((n >> 8) & 0xff) / 255,
    b: (n & 0xff) / 255,
  };
}

/** Serialise an {@link RgbColor} (0..1, clamped) into a `#rrggbb` string. */
export function rgbToHex(c: RgbColor): string {
  const ch = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v * 255)))
      .toString(16)
      .padStart(2, "0");
  return `#${ch(c.r)}${ch(c.g)}${ch(c.b)}`;
}

/**
 * True iff `value` is a 6-digit hex colour string (`#rrggbb` or bare `rrggbb`,
 * case-insensitive). One truth for the "is this a true-colour string?" test
 * shared by the property-grid Color cell (true-colour swatch) and the colour
 * picker (initial-tab seeding) — both used to reimplement this regex.
 *
 * Mirrors the `#rrggbb`/bare-6-hex branch that `coerceColor`
 * (`drawing-model/color.ts`) accepts on the apply path, so a value this returns
 * `true` for round-trips to `{ kind: "rgb" }`.
 */
export function isHexColorString(value: string): boolean {
  const body = value.startsWith("#") ? value.slice(1) : value;
  return /^[0-9a-f]{6}$/i.test(body);
}

/** A single 0..255 RGB channel triple — the integer counterpart of {@link RgbColor}. */
export interface Rgb255 {
  r: number;
  g: number;
  b: number;
}

/**
 * Parse a `#rrggbb` (optional leading `#`) hex string into 0..255 integer
 * channels. Returns black (`{0,0,0}`) for a malformed input. Used by the True
 * Color picker tab to seed its R/G/B spin fields from an existing hex value.
 */
export function hexToRgb255(hex: string): Rgb255 {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1]!, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

/**
 * Serialise 0..255 integer channels (clamped, rounded) into a lowercase
 * `#rrggbb` string — the canonical form `colorToLegacyString` emits and
 * `coerceColor` accepts. Used by the True Color picker tab's OK affordance.
 */
export function rgb255ToHex(c: Rgb255): string {
  const ch = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, "0");
  return `#${ch(c.r)}${ch(c.g)}${ch(c.b)}`;
}
